import { ScriptableContext } from "chart.js";
import { useMemo } from "react";
import { getVisualizationColors } from "../../../theme/colors";
import { ChartProcessedData, ChartSeries, RedoChartStyle } from "./types";

export const useChartData = (
  series: ChartSeries[],
  chartStyle: RedoChartStyle,
  dateFormatter: (date: Temporal.Instant) => string,
  disableGradient = false,
  showPoints = true,
  lineTension?: number,
  solidFill = false,
): ChartProcessedData => {
  // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
  return useMemo(() => {
    const colors = getVisualizationColors(series.length);

    const coloredSeries = series.map((s, i) => ({
      ...s,
      color: s.color || colors[i],
    }));

    const dateMap = new Map<
      string,
      { formatted: string; instant: Temporal.Instant }
    >();

    for (const s of coloredSeries) {
      for (const { date } of s.data) {
        const key = date.toString();
        if (!dateMap.has(key)) {
          dateMap.set(key, { formatted: dateFormatter(date), instant: date });
        }
      }
    }

    const dateEntries = [...dateMap.entries()]
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([key, val]) => ({ key, ...val }));

    const labels = dateEntries.map((d) => d.formatted);

    const normalizedSeries = coloredSeries.map((s) => {
      const valueByDate = new Map(
        s.data.map((d) => [d.date.toString(), d.value]),
      );

      const normalizedData = dateEntries.map((d) => {
        const raw = valueByDate.get(d.key);
        return { date: d.instant, value: raw === undefined ? 0 : raw };
      });

      return { ...s, data: normalizedData };
    });

    const datasets = normalizedSeries.map((s) => {
      const seriesFill = s.fill !== undefined ? s.fill : true;
      const useFill =
        chartStyle === "line" && (solidFill || !disableGradient) && seriesFill;

      return {
        label: s.name,
        data: s.data.map((d) => d.value),
        backgroundColor: (ctx: ScriptableContext<"bar" | "line">) => {
          if (chartStyle !== "line" || !seriesFill) {
            return s.color;
          }
          if (solidFill) {
            return s.color;
          }
          if (disableGradient) {
            return s.color;
          }

          const { chartArea, ctx: canvas } = ctx.chart;
          if (!chartArea) {
            return s.color;
          }

          const gradient = canvas.createLinearGradient(
            0,
            chartArea.top,
            0,
            chartArea.bottom,
          );
          gradient.addColorStop(0, `${s.color}40`);
          gradient.addColorStop(1, `${s.color}00`);
          return gradient;
        },
        borderColor: s.color,
        borderWidth: chartStyle === "line" ? 1.5 : undefined,
        borderDash: s.dashed ? [5, 5] : undefined,
        tension: chartStyle === "line" ? (lineTension ?? 0.4) : undefined,
        pointRadius: chartStyle === "line" ? (showPoints ? 5 : 0) : undefined,
        pointHoverRadius:
          chartStyle === "line" ? (showPoints ? 7 : 0) : undefined,
        pointBackgroundColor: chartStyle === "line" ? s.color : undefined,
        pointBorderColor: chartStyle === "line" ? "#FFFFFF" : undefined,
        pointBorderWidth: chartStyle === "line" ? 2 : undefined,
        fill: useFill,
        borderRadius: chartStyle === "bar" ? 4 : undefined,
      };
    });

    return { labels, datasets, normalizedSeries };
  }, [
    series,
    chartStyle,
    dateFormatter,
    disableGradient,
    showPoints,
    lineTension,
    solidFill,
  ]);
};
